import i18n from "@helpers/lib/i18n";
import { isAnswered } from "@memorang/helpers/item";
import type {
	AnswerKeySelection,
	BackendSessionResponse,
	BaseItemSchema,
	Choice,
	Grading,
	InstructionType,
	NumChoicesRequired,
	Slide,
	StimulusMetadata,
} from "@memorang/types";
import { SESSION_EVENTS } from "../constants";
import type { Answers, SessionEvent } from "../hooks/types";
import { isIntroScreen } from "./intro-screens";

const formatInstruction = (
	instruction: string | InstructionType | undefined,
): string => {
	if (!instruction) {
		return "";
	}
	if (typeof instruction === "string") {
		return instruction;
	}
	return instruction.text ?? "";
};

export const getItemInstruction = (item: BaseItemSchema): string => {
	const { content, stimulus } = item;
	const candidates = [
		content.stimulus?.instruction,
		stimulus?.stimulus?.instruction,
		stimulus?.instruction,
		content.instruction,
	];

	for (const candidate of candidates) {
		const formatted = formatInstruction(candidate);
		if (formatted) {
			return formatted;
		}
	}

	return "";
};

export const diagnosticTagIds = {
	readingListening: 1000,
	speakingWriting: 1001,
};

export const getIntlExplanationForChoice = (choice: Choice) => {
	switch (i18n.language) {
		case "zh-CN":
			return choice?.explanation_mandarin;
		case "ja-JP":
			return choice?.explanation_japanese;
		default:
			return choice?.explanation;
	}
};
export const findLastUpdatedItemIndex = (
	items: BaseItemSchema[][],
): { lastUpdatedBlockIndex: number; lastUpdatedItemIndex: number } => {
	const lastUpdatedBlockIndex = items.findIndex((block) =>
		block.some((item) => item.lastUpdated && item.answer != null),
	);
	const lastUpdatedItemIndex =
		items[lastUpdatedBlockIndex]
			?.map((item, index) => ({
				hasUpdate: Boolean(item.lastUpdated && item.answer != null),
				index,
			}))
			?.filter((item) => item.hasUpdate)
			?.at(0)?.index || 0;

	return {
		lastUpdatedBlockIndex:
			lastUpdatedBlockIndex >= 0 ? lastUpdatedBlockIndex : 0,
		lastUpdatedItemIndex:
			lastUpdatedItemIndex >= 0 ? lastUpdatedItemIndex + 1 : 0,
	};
};

type ContentLike = BaseItemSchema["content"] | undefined;

const mergeChildContentWithParent = (
	child: ContentLike,
	parent: ContentLike,
): BaseItemSchema["content"] => {
	const base = { ...(parent || {}), ...(child || {}) };

	const childStim = child?.stimulus;
	const parentStim = parent?.stimulus;
	if (childStim || parentStim) {
		base.stimulus = { ...(parentStim || {}) };
		base.stem = {
			text: childStim?.text || child?.stem?.text || "",
		};
		base.instruction = childStim?.instruction || parentStim?.instruction || "";
	}

	const childMedia = child?.media;
	const parentMedia = parent?.media;
	if (!(childMedia && childMedia.length > 0) && parentMedia) {
		base.media = parentMedia;
	}

	return base;
};

const propagateAutoPlayChildAudio = (
	child: BaseItemSchema["response"],
	parent: BaseItemSchema["response"],
): BaseItemSchema["response"] => {
	if (!(parent?.config?.autoPlayChildAudio && child)) {
		return child;
	}
	return {
		...child,
		config: { ...child.config, autoPlayChildAudio: true },
	};
};

const flattenSessionItems = (
	sessionItems?: BackendSessionResponse["sessionItems"],
	isSummativeTest = true,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
): BaseItemSchema[][] => {
	const normalizedSessionItems = Array.isArray(sessionItems)
		? sessionItems
		: [];

	if (normalizedSessionItems.length === 0) {
		return [[]];
	}

	const hasBlocks =
		isSummativeTest &&
		normalizedSessionItems.some((item) => item.metadata?.section);

	if (hasBlocks) {
		const blocksMap = new Map<string, BaseItemSchema[]>();
		const blockKeyMeta = new Map<string, { part: number; section: number }>();

		for (const item of normalizedSessionItems) {
			const meta = item.metadata ?? item.stimulus?.metadata;
			const section = meta?.section;
			if (section === undefined) {
				continue;
			}
			const part = meta?.part ?? 0;
			const blockKey = `${part}-${section}`;
			if (!blocksMap.has(blockKey)) {
				blocksMap.set(blockKey, []);
				blockKeyMeta.set(blockKey, {
					part: Number(part),
					section: Number(section),
				});
			}

			if (item.recordType === "SCENARIO") {
				const mappedItems = item.items!.map((setItem, index) => ({
					...setItem,
					stimulus: item.stimulus || item.content,
					response: propagateAutoPlayChildAudio(
						setItem.response,
						item.response,
					),
					currentStimulusItemIndex: index + 1,
					numTotalStimulusItems: item.items!.length,
					blockId: String(section),
					recordType: item.recordType,
					stimulusMetadata: item.metadata as unknown as StimulusMetadata,
				}));

				blocksMap.get(blockKey)!.push(...mappedItems);
			} else {
				blocksMap.get(blockKey)!.push(item);
			}
		}

		const sortByNumberThenString = (a: number, b: number) =>
			Number.isNaN(a) || Number.isNaN(b) ? 0 : a - b;

		const sortedKeys = Array.from(blocksMap.keys()).sort((a, b) => {
			const ma = blockKeyMeta.get(a)!;
			const mb = blockKeyMeta.get(b)!;
			return (
				sortByNumberThenString(ma.part, mb.part) ||
				sortByNumberThenString(ma.section, mb.section)
			);
		});

		const result = sortedKeys.map((key) => blocksMap.get(key)!);

		return result;
	}
	const result = normalizedSessionItems.flatMap(
		(setOrItem): BaseItemSchema[] => {
			if (setOrItem.recordType === "SCENARIO") {
				const resolvedStimulus = setOrItem.stimulus || setOrItem.content;
				return (
					setOrItem.items?.map((item, index) => ({
						...item,
						stimulus: resolvedStimulus,
						content: mergeChildContentWithParent(
							item.content,
							resolvedStimulus,
						),
						response: propagateAutoPlayChildAudio(
							item.response,
							setOrItem.response,
						),
						currentStimulusItemIndex: index + 1,
						numTotalStimulusItems: setOrItem.items?.length,
						recordType: setOrItem.recordType,
						stimulusMetadata: setOrItem.metadata as unknown as StimulusMetadata,
					})) ?? []
				);
			}
			return [setOrItem];
		},
	);

	return [result];
};

const getSlidesData = (slides?: Slide[]) => {
	return (
		slides
			?.filter((slide) => slide.media?.length && slide.media?.length > 0)
			.map((slide) => ({
				image:
					slide.media?.find(
						(m) =>
							m.type === "image" || m.type === "IMAGE" || m.type === "images",
					)?.url ?? "a",
				audio:
					slide.media?.find((m) => m.type === "audio" || m.type === "AUDIO")
						?.url ?? "",
			})) ?? []
	);
};

const getIsSlidesHasPassage = (slides?: Slide[]) => {
	return slides?.some((slide) => slide?.passage != null);
};

const resolveChoiceIdFromAnswerKey = (
	choices: Choice[],
	choiceId: string,
): string | undefined => {
	const byId = choices.find((c) => c.id === choiceId);
	if (byId) {
		return byId.id;
	}
	const ordinal = Number.parseInt(choiceId, 10);
	if (Number.isFinite(ordinal) && ordinal > 0) {
		return choices[ordinal - 1]?.id;
	}
	if (/^[A-Z]$/.test(choiceId)) {
		return choices[choiceId.charCodeAt(0) - "A".charCodeAt(0)]?.id;
	}
	return undefined;
};

export const getCorrectAnswerIdsFromAnswerKey = (
	choices: Choice[],
	scoring: AnswerKeySelection,
): string[] => {
	return scoring.answerKey
		.filter((key) => key.isCorrect)
		.map((key) => resolveChoiceIdFromAnswerKey(choices, key.choiceId))
		.filter((id): id is string => id !== undefined);
};

export const getResolvedCorrectAnswerIds = (
	choices: Choice[],
	scoring?: Grading,
): string[] => {
	if (scoring?.type === "ANSWER_KEY_SELECTION" || scoring?.type === "choice") {
		return getCorrectAnswerIdsFromAnswerKey(choices, scoring);
	}
	return choices.filter((c) => c.isCorrect).map((c) => c.id);
};

export const getEarnedPointsFromAnswerKey = (
	choices: Choice[],
	scoring: AnswerKeySelection,
	selectedChoiceIds?: string[],
): number => {
	if (!selectedChoiceIds?.length) {
		return 0;
	}
	const selectedSet = new Set(selectedChoiceIds);

	if (scoring.kind === "ANSWER_KEY_BINARY") {
		const allCorrectSelected = scoring.answerKey
			.filter((k) => k.isCorrect)
			.every((k) => {
				const resolvedId = resolveChoiceIdFromAnswerKey(choices, k.choiceId);
				return resolvedId != null && selectedSet.has(resolvedId);
			});
		const noWrongSelected = scoring.answerKey
			.filter((k) => !k.isCorrect)
			.every((k) => {
				const resolvedId = resolveChoiceIdFromAnswerKey(choices, k.choiceId);
				return resolvedId == null || !selectedSet.has(resolvedId);
			});
		return allCorrectSelected && noWrongSelected ? (scoring.points ?? 1) : 0;
	}

	return scoring.answerKey.reduce((sum, key) => {
		const resolvedId = resolveChoiceIdFromAnswerKey(choices, key.choiceId);
		if (resolvedId && selectedSet.has(resolvedId)) {
			return sum + (key.points ?? 0);
		}
		return sum;
	}, 0);
};

// Negative marking: +points for each correct word,
// -1 (or scoring.incorrectPoints) for each wrong word,
// min floored at scoring.minPoints (default 0).
// Answer key lists only correct words.
export const getHighlightIncorrectWordsEarnedPoints = (
	choices: Choice[],
	scoring: AnswerKeySelection,
	selectedChoiceIds?: string[],
): number => {
	const minPoints = scoring.minPoints ?? 0;
	if (!selectedChoiceIds?.length) {
		return Math.max(minPoints, 0);
	}
	const incorrectPoints = scoring.incorrectPoints ?? -1;
	const correctPointsById = new Map<string, number>();
	for (const key of scoring.answerKey) {
		if (!key.isCorrect) {
			continue;
		}
		const resolvedId = resolveChoiceIdFromAnswerKey(choices, key.choiceId);
		if (resolvedId != null) {
			correctPointsById.set(resolvedId, key.points);
		}
	}
	const earnedPoints = selectedChoiceIds.reduce(
		(acc, id) => acc + (correctPointsById.get(id) ?? incorrectPoints),
		0,
	);
	return Math.max(minPoints, earnedPoints);
};

export const getNumChoicesRequired = (
	scoring: AnswerKeySelection,
): NumChoicesRequired | undefined => {
	if (!scoring.selection) {
		return undefined;
	}
	return { min: scoring.selection.min, max: scoring.selection.max };
};

export type SessionPart = {
	partIndex: number;
	partId: number;
	partName: string;
	blockIndices: number[];
};

const buildSessionParts = (blocks: BaseItemSchema[][]): SessionPart[] => {
	const parts: SessionPart[] = [];
	const byPartId = new Map<number, SessionPart>();

	blocks.forEach((block, blockIndex) => {
		const anchorItem = block.find((item) => !isIntroScreen(item));

		// Intro-only blocks are not sections of a Part; the intro screens'
		// section labels and progress come from the Part's real blocks only.
		if (!anchorItem) {
			return;
		}

		const meta = anchorItem.stimulusMetadata ?? anchorItem.metadata;
		const partId = Number(meta?.part ?? 0);
		const partName = meta?.partName ?? "";

		let part = byPartId.get(partId);
		if (!part) {
			part = {
				partIndex: parts.length,
				partId,
				partName,
				blockIndices: [],
			};
			byPartId.set(partId, part);
			parts.push(part);
		}
		part.blockIndices.push(blockIndex);
	});

	return parts;
};

export const getPartIndexForBlock = (
	parts: SessionPart[],
	blockIndex: number,
): number => parts.findIndex((p) => p.blockIndices.includes(blockIndex));

export const findSessionItemById = (
	sessionItems: BaseItemSchema[][],
	itemId: number,
): BaseItemSchema | undefined => {
	for (const block of sessionItems) {
		const found = block.find((item) => item.id === itemId);
		if (found) {
			return found;
		}
	}
	return undefined;
};

export const withItemViewedFallback = (
	sessionEvents: SessionEvent[] | undefined,
): SessionEvent[] => {
	if (sessionEvents?.length) {
		return sessionEvents;
	}
	return [{ ts: new Date().toISOString(), name: SESSION_EVENTS.ITEM_VIEWED }];
};

// Skipping an item still records an answer, so map size counts skips as
// answers. Only a response the user actually gave makes a session finishable.
export const hasAnsweredAnyItem = (answers?: Answers): boolean =>
	answers != null && [...answers.values()].some(isAnswered);

export {
	buildSessionParts,
	flattenSessionItems,
	getIsSlidesHasPassage,
	getSlidesData,
};
